import { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  buildStreamUrl,
  EventStreamClient,
  invalidateAreas,
  invalidateForEvent,
  POLL_AREAS,
  type EventSourceLike,
  type StreamStatus,
} from '@/api/eventStream';
import { useAuthStore } from '@/auth/authStore';
import { useTenantId } from '@/auth/useAuth';
import { env } from '@/lib/env';

export const POLL_INTERVAL_MS = 10_000;

/**
 * Subscribes to `/v1/stream` for the current tenant scope and invalidates the matching TanStack queries per event.
 * While the stream is not open, ops queries are re-fetched every 10 s instead (polling fallback).
 */
export function useEventStream(createSource?: (url: string) => EventSourceLike): StreamStatus {
  const qc = useQueryClient();
  const signedIn = useAuthStore((s) => s.accessToken !== null);
  const tenantId = useTenantId();
  const [status, setStatus] = useState<StreamStatus>('connecting');

  useEffect(() => {
    if (!signedIn) return undefined;
    const client = new EventStreamClient({
      url: () => buildStreamUrl(env.apiBaseUrl, useAuthStore.getState().accessToken, tenantId),
      onEvent: (e) => void invalidateForEvent(qc, e),
      onStatus: setStatus,
      createSource,
    });
    client.start();
    return () => client.stop();
  }, [signedIn, tenantId, qc, createSource]);

  useEffect(() => {
    if (status === 'open') return undefined;
    const handle = setInterval(() => void invalidateAreas(qc, POLL_AREAS), POLL_INTERVAL_MS);
    return () => clearInterval(handle);
  }, [status, qc]);

  return status;
}
